/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState } from 'react';
import { HiArrowNarrowRight } from 'react-icons/hi';
import { GrFormNext, GrFormPrevious } from 'react-icons/gr';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import {
  generateSlugifyUrl, getPaginationItems, itemsPerPage, maxLength,
} from '../../utils';
import { OpenJob } from '../../types';

interface ListViewCardProps {
  jobs: OpenJob[];
  search: string;
}

export default function ListViewCards(props: ListViewCardProps) {
  const { jobs, search } = props;
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(jobs.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = jobs.slice(indexOfFirstItem, indexOfLastItem);

  const pageNums = getPaginationItems(currentPage, totalPages, maxLength);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  return (
    <div className="space-y-4 mt-5">
      {
        currentItems.map((job: OpenJob) => (
          // eslint-disable-next-line jsx-a11y/interactive-supports-focus
          <div
            onClick={() => {
              navigate(`/${job.organizationId}${generateSlugifyUrl(job)}`);
            }}
            role="button"
            key={job.jobId}
            className="block relative bg-[#F4F4F4] rounded-xl cursor-pointer overflow-hidden"
          >
            <div className="left-0 z-10 absolute bg-primary rounded-tl-full rounded-bl-full w-2 h-full" />
            <div className="relative z-20 flex flex-wrap items-center py-2 pr-2 pl-8">
              <p className="relative z-20 w-6/12 font-bold text-black text-lg basis-[68%] md:basis-auto">
                {job.jobTitle}
              </p>
              <p className="w-4/12 font-light text-base uppercase basis-[50%] md:basis-auto">
                {job.location}
              </p>
              <div className="flex justify-end items-center gap-5 w-2/12 basis-[50%] md:basis-auto">
                <p className="text-sm">
                  Apply
                </p>
                <div className="flex justify-center items-center bg-[#D9D9D9]/20 rounded-md w-10 h-10">
                  <HiArrowNarrowRight className="w-4 h-4 text-gray-300" />
                </div>
              </div>
            </div>
          </div>
        ))
      }

      <div className="flex flex-wrap justify-between gap-y-4 mt-5">
        <div>
          {(currentPage * itemsPerPage) > jobs.length ? jobs.length : (currentPage * itemsPerPage)}
          {' '}
          of
          {jobs.length}
        </div>

        {jobs.length > itemsPerPage ? (
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              type="button"
              onClick={() => setCurrentPage((prevPage) => prevPage - 1)}
              disabled={currentPage === 1}
              className="w-6 h-6 cursor-pointer disabled:hover:cursor-not-allowed"
            >
              <GrFormPrevious
                className={currentPage === 1 ? '[&>*]:stroke-[#B0B0B0]' : ''}
              />
            </button>
            {pageNums.map((pageNumber) => (
              <button
                type="button"
                key={pageNumber}
                disabled={Number.isNaN(pageNumber)}
                onClick={() => setCurrentPage(pageNumber)}
                className={twMerge(
                  'pb-6 w-6 h-6',
                  pageNumber === currentPage ? 'font-bold border-b border-black' : 'font-normal',
                )}
              >
                {Number.isNaN(pageNumber) ? '...' : pageNumber}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage((prevPage) => prevPage + 1)}
              disabled={currentPage === totalPages}
              className="w-6 h-6 cursor-pointer disabled:hover:cursor-not-allowed"
            >
              <GrFormNext
                className={currentPage === totalPages ? '[&>*]:stroke-[#B0B0B0]' : ''}
              />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
